import {
  DeviceQuantityMap,
  Dispatch,
  OBAvailableHardware,
  OBAWSRegions,
  OBCloudSensorApplication,
  OBCloudSensors,
  OBContact,
  OBEndpointConfig,
  OBFinalizationStatus,
  OBGCPLicenses,
  OBGenericSensor,
  OBGeoLoginCountry,
  OBInternetExposure,
  OBLogSource,
  OBLogSourceMFA,
  OBO365Licenses,
  OBScanner,
  OBSensor,
  OBSensorConfigurations,
  OBSFLicenses,
  OBSite,
  OBSyncResults,
  OBTeamContacts,
  OBVirtualizationSoftware,
  OBVirtualScanner,
  OBVirtualSensorConfig,
  RawOBAvailableEndpoints,
  RawOBAvailableVirtualSensors,
  SensorCapabilities,
  SensorCapability,
  OnboardingStatus,
  InvalidTarget,
  OBIManageLicenses,
} from '../types';
import { OBRawProducts } from '../reducers/onboarding/utils';
import { State } from '../reducers/onboarding';
import { apiRequest } from '../APIClient';

/**
 * a contact model closer to the one defined by the onboarding API
 */
export type RawOBContact = {
  first_name: string;
  last_name: string;
  escalation: {
    incident: boolean;
  };
  intelligence_reports: boolean;
  is_mailing_list: boolean;
  email: string;
  access: {
    is_risk_portal_user: boolean;
    is_portal_user: boolean;
    is_log_search_user: boolean;
    is_managed_awareness_user: boolean;
    is_activate_vlc_user: boolean;
    is_mycyber_dashboard_user: boolean;
  };
  phone: {
    id: string;
    type: 'office' | 'mobile';
    number: string;
    extension?: string;
  }[];
  is_admin: boolean;
  is_primary_contact: boolean;
  is_secondary_contact: boolean;
  is_msp: boolean;
  is_shipping_contact?: boolean;
  is_executive_sponsor: boolean;
  role: string | null;
  id: string;
};

export type RawOBContactSection = {
  contacts: ReadonlyArray<RawOBContact>;
  completed: boolean;
  finalized: OBFinalizationStatus;
};

export type RawOBExternal = {
  outbound_allowed: boolean;
  ssl_disabled: boolean;
  external_scan_allowed: boolean;
  external_scan_info: {
    addresses: string[];
    dns: string[];
  };
  no_external_hosts: boolean;
  client_vpn: any;
  domain_ownership_confirmed: boolean;
  completed: boolean;
  finalized: OBFinalizationStatus;
  invalid_targets: InvalidTarget[];
};

export type RawOBClientVPNInfo = {
  client_vpn: string | null;
  two_factor_auth: OBLogSourceMFA | '';
};

export type RawOBLogSource = Omit<OBLogSource, 'vpn' | 'mfa'> & {
  client_vpn: RawOBClientVPNInfo;
};

export type RawOBSite = {
  id: string;
  name: string;
  address: {
    id: string;
    country: string;
    street: string;
    street2: string;
    city: string;
    region: string;
    postcode: string;
  };
  time_zone: string;
  firewall_models: string[];
  switch_models: string[];
  network_segments: string[];
  security_devices: string[];
  monitoring_blacklist: string[];
  scanning_blacklist: string[];
  active_directory_controllers: number;
  adc_is_dhcp: boolean;
  adc_is_dns: boolean;
  third_party_site: boolean;
  oldest_windows_version: string;
  log_sources: RawOBLogSource;
  notes: string;
  finalized: OBFinalizationStatus;
};

export type RawOBSiteSection = {
  sites: ReadonlyArray<RawOBSite>;
  completed: boolean;
  finalized: OBFinalizationStatus;
  configure_connectwise: boolean;
};

export type RawOBSensor = {
  id: string;
  name: string;
  location_id: string;
  network_configuration: {
    address: string;
    subnet_mask: string;
    gateway: string;
  } | null;
  primary_dns: string;
  secondary_dns: string | null;
  model: string;
  series: string;
  configuration: OBSensorConfigurations;
  recommendation_overridden: boolean;
  shipping_address: {
    id: string;
    country: string;
    street: string;
    street2: string;
    city: string;
    region: string;
    postcode: string;
  };
  shipping_contact: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: { number: string; type: 'office' | 'mobile' }[];
  };
  shipping_notes: string;
  need_rackmounts: boolean;
  capabilities: Record<string, string>;
  configuration_task_id: number;
};

export type RawOBScanner = {
  id: string;
  name: string;
  location_id: string;
  network_configuration: {
    address: string;
    subnet_mask: string;
    gateway: string;
  } | null;
  primary_dns: string | null;
  secondary_dns: string | null;
  model: string;
  vanilla?: boolean;
  shipping_address: {
    id: string;
    country: string;
    street: string;
    street2: string;
    city: string;
    region: string;
    postcode: string;
  };
  shipping_contact: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: { number: string; type: 'office' | 'mobile' }[];
  };
  shipping_notes: string;
  configuration_task_id: number;
};

export type RawOBCloudSensors = {
  amazon: {
    regions: OBAWSRegions[];
    cloudwatch: boolean;
    cloudtrail: boolean;
    guardduty: boolean;
    acl: boolean;
    eks: boolean;
    completed: boolean;
  };
  azure: {
    log_analytics: boolean;
    security_center: boolean;
    sentinel: boolean;
    completed: boolean;
  };
  office365: { dlp: boolean; licenses: OBO365Licenses[]; completed: boolean };
  salesforce: { licenses: OBSFLicenses[]; completed: boolean };
  gcp: { licenses: OBGCPLicenses[] };
  geo_login: { countries: OBGeoLoginCountry[]; completed: boolean };
  imanage: { licenses: OBIManageLicenses[]; completed: boolean };
  finalized: OBFinalizationStatus;
};

export type RawOBVirtualScanner = {
  software: OBVirtualizationSoftware;
  acknowledged: boolean;
  no_virtual_scanners: boolean;
  completed: boolean;
};

export type RawOBEndpointConfig = {
  on_prem_epps: string[] | null;
  cloud_epps: string[] | null;
  no_on_prem_epps: boolean;
  no_cloud_epps: boolean;
  sysmon_installed: boolean;
  no_agents: boolean;
  windows_endpoints: number;
  mac_endpoints: number;
  linux_endpoints: number;
  notes: string;
  agent_completed: boolean;
  epp_completed: boolean;
  completed: boolean;
  finalized: OBFinalizationStatus;
};

export type RawOBVirtualSensorConfig = {
  hypervisor: string[] | null;
  virtualization_requirements: boolean;
  no_virtual_sensor: boolean;
  completed: boolean;
};

export type OnboardingAction =
  | OnboardingSiteAction
  | SetOnboardingStatusActions
  | SetContactsActions
  | SetContactAction
  | SetContactsLoadingAction
  | SetLastUpdatedContactAction
  | RevertContactsAction
  | EditInternetExposureAction
  | SetInternetExposureAction
  | DeleteSensorAction
  | SetSensorsLoadingAction
  | EditSensorAction
  | SetSensorsAction
  | AddSensorAction
  | SetScannersAction
  | AddScannerAction
  | EditScannerAction
  | DeleteScannerAction
  | SetScannersLoadingAction
  | EditCloudSensorsAction
  | SetCloudSensorsAction
  | EditVirtualScannerAction
  | SetVirtualScannerAction
  | ToggleCompleteAction
  | ToggleCloudSensorCompleteAction
  | SetOnboardingSyncResultsAction
  | SetOnboardingTeamContactsAction
  | SetOnboardingSyncModalOpen
  | SetOnboardingProducts
  | SetOnboardingAvailableHardware
  | SetOnboardingAvailableHardwareQuantity
  | SetOnboardingAvailableEndpoints
  | SetVirtualSensorAction
  | EditVirtualSensorAction
  | SetOnboardingAvailableVirtualSensors
  | SetEndpointAction
  | EditEndpointAction
  | SetOnboardingAvailableGenericSensors
  | SetOnboardingSensorCapabilities;

type SetOnboardingStatusActions = {
  readonly type: 'set_onboarding_status';
  readonly status: OnboardingStatus;
};

/**
 * Contacts
 */
type SetContactsActions = {
  readonly type: 'set_contacts';
  readonly contacts: RawOBContactSection;
};

type SetContactAction = {
  readonly type: 'set_contact';
  readonly uuid: string;
  readonly contact: OBContact;
};

type SetContactsLoadingAction = {
  readonly type: 'set_contacts_loading';
  readonly loading: boolean;
};

type SetLastUpdatedContactAction = {
  readonly type: 'set_last_updated_contact';
  readonly uuid: string;
  readonly contact: OBContact;
};

type RevertContactsAction = {
  readonly type: 'revert_contacts';
};

/**
 * Internet Exposure
 */
type SetInternetExposureAction = {
  readonly type: 'set_internet_exposure';
  readonly internetExposure: RawOBExternal;
};

type EditInternetExposureAction = {
  readonly type: 'edit_internet_exposure';
  readonly internetExposure: OBInternetExposure;
};

/**
 * Sensors
 */
type SetSensorsAction = {
  readonly type: 'set_sensors';
  readonly sensors: {
    sensors: RawOBSensor[];
    completed: boolean;
  };
};

type EditSensorAction = {
  readonly type: 'edit_sensor';
  readonly sensor: OBSensor;
  readonly uuid: string;
};

type DeleteSensorAction = {
  readonly type: 'delete_sensor';
  readonly uuid: string;
};

type SetSensorsLoadingAction = {
  readonly type: 'set_sensors_loading';
  readonly loading: boolean;
};

export type AddSensorAction = {
  readonly type: 'add_sensor';
  readonly sensor: OBSensor;
  readonly uuid: string;
};

/**
 * Sites
 */
type SetSitesAction = {
  readonly type: 'set_sites';
  readonly sites: RawOBSiteSection;
};

type SetSiteAction = {
  readonly type: 'set_site';
  readonly site: OBSite;
  readonly uuid: string;
};

type DeleteSiteAction = {
  readonly type: 'delete_site';
  readonly uuid: string;
};

export type OnboardingSiteAction =
  | SetSitesAction
  | SetSiteAction
  | DeleteSiteAction;

/**
 * Cloud Sensors
 */
type SetCloudSensorsAction = {
  readonly type: 'set_cloud_sensors';
  readonly cloudSensors: RawOBCloudSensors;
};
type EditCloudSensorsAction = {
  readonly type: 'edit_cloud_sensors';
  readonly cloudSensors: OBCloudSensors;
};

/**
 * Risk Scanners
 */
type SetScannersAction = {
  readonly type: 'set_scanners';
  readonly scanners: { scanners: RawOBScanner[]; completed: boolean };
};

type SetScannersLoadingAction = {
  readonly type: 'set_scanners_loading';
  readonly loading: boolean;
};

type EditScannerAction = {
  readonly type: 'edit_scanner';
  readonly scanner: OBScanner;
  readonly uuid: string;
};

type DeleteScannerAction = {
  readonly type: 'delete_scanner';
  readonly uuid: string;
};

type AddScannerAction = {
  readonly type: 'add_scanner';
  readonly scanner: OBScanner;
};

/**
 * Virtual Scanner
 */
type SetVirtualScannerAction = {
  readonly type: 'set_virtual_scanner';
  readonly setting: RawOBVirtualScanner;
};
type EditVirtualScannerAction = {
  readonly type: 'edit_virtual_scanner';
  readonly setting: OBVirtualScanner;
};

/**
 * Endpoint Config
 */
export type SetOnboardingAvailableEndpoints = {
  readonly type: 'set_onboarding_available_endpoints';
  readonly availableEndpoints: RawOBAvailableEndpoints;
};

type SetEndpointAction = {
  readonly type: 'set_endpoint';
  readonly config: RawOBEndpointConfig;
};
type EditEndpointAction = {
  readonly type: 'edit_endpoint';
  readonly config: OBEndpointConfig;
};

/**
 * Virtual Sensor Config
 */
export type SetOnboardingAvailableVirtualSensors = {
  readonly type: 'set_onboarding_available_virtual_sensors';
  readonly availableVirtualSensors: RawOBAvailableVirtualSensors;
};

type SetVirtualSensorAction = {
  readonly type: 'set_virtual_sensor';
  readonly config: RawOBVirtualSensorConfig;
};
type EditVirtualSensorAction = {
  readonly type: 'edit_virtual_sensor';
  readonly config: OBVirtualSensorConfig;
};

type CompletableSection = Pick<
  State,
  | 'contactsSection'
  | 'internetExposure'
  | 'sitesSection'
  | 'sensorsSection'
  | 'scannersSection'
  | 'virtualScanner'
  | 'endpointSection'
  | 'virtualSensorSection'
>;

export type ToggleCompleteAction = {
  readonly type: 'toggle_complete';
  readonly key: keyof CompletableSection;
};

type ToggleCloudSensorCompleteAction = {
  readonly type: 'toggle_cloud_sensor_complete';
  readonly key: OBCloudSensorApplication;
};

type SetOnboardingSyncResultsAction = {
  readonly type: 'set_sync_results';
  readonly results: OBSyncResults | null;
};

type SetOnboardingTeamContactsAction = {
  readonly type: 'set_onboarding_team_contacts';
  readonly contacts: OBTeamContacts;
};

type SetOnboardingSyncModalOpen = {
  readonly type: 'set_onboarding_sync_modal_open';
  readonly open: boolean;
};

type SetOnboardingProducts = {
  readonly type: 'set_onboarding_products';
  readonly products: OBRawProducts;
};

export type SetOnboardingAvailableHardware = {
  readonly type: 'set_onboarding_available_hardware';
  readonly availableHardware: OBAvailableHardware;
};

export type SetOnboardingAvailableHardwareQuantity = {
  readonly type: 'set_onboarding_available_hardware_quantity';
  readonly hardwareQuantity: DeviceQuantityMap;
};

export type SetOnboardingAvailableGenericSensors = {
  readonly type: 'set_onboarding_available_generic_sensors';
  readonly availableGenericSensors: OBGenericSensor[];
};

export type SetOnboardingSensorCapabilities = {
  readonly type: 'set_onboarding_sensor_capabilities';
  readonly sensorCapabilities: SensorCapabilities;
};

export const ONBOARDING_ACTIONS = {
  /**
   * STATUS
   */
  SET_ONBOARDING_STATUS: (
    data: OnboardingStatus,
  ): SetOnboardingStatusActions => ({
    type: 'set_onboarding_status',
    status: data,
  }),

  /**
   * Contacts
   */
  SET_CONTACTS: (data: RawOBContactSection): SetContactsActions => ({
    type: 'set_contacts',
    contacts: data,
  }),
  SET_CONTACT: (uuid: string, contact: OBContact): SetContactAction => ({
    type: 'set_contact',
    uuid,
    contact,
  }),
  SET_CONTACTS_LOADING: (loading: boolean): SetContactsLoadingAction => ({
    type: 'set_contacts_loading',
    loading,
  }),
  SET_LAST_UPDATED_CONTACTS: (
    uuid: string,
    contact: OBContact,
  ): SetLastUpdatedContactAction => ({
    type: 'set_last_updated_contact',
    uuid,
    contact,
  }),
  REVERT_CONTACTS: (): RevertContactsAction => ({
    type: 'revert_contacts',
  }),

  /**
   * Internet Exposure
   */
  SET_INTERNET_EXPOSURE: (data: RawOBExternal): SetInternetExposureAction => ({
    type: 'set_internet_exposure',
    internetExposure: data,
  }),
  EDIT_INTERNET_EXPOSURE: (
    internetExposure: OBInternetExposure,
  ): EditInternetExposureAction => ({
    type: 'edit_internet_exposure',
    internetExposure,
  }),

  /**
   * Sensor
   */
  DELETE_SENSOR: (uuid: string): DeleteSensorAction => ({
    type: 'delete_sensor',
    uuid,
  }),
  EDIT_SENSOR: (uuid: string, sensor: OBSensor): EditSensorAction => ({
    type: 'edit_sensor',
    sensor,
    uuid,
  }),
  SET_SENSORS: (data: {
    sensors: RawOBSensor[];
    completed: boolean;
  }): SetSensorsAction => ({
    type: 'set_sensors',
    sensors: data,
  }),
  SET_SENSORS_LOADING: (loading: boolean): SetSensorsLoadingAction => ({
    type: 'set_sensors_loading',
    loading,
  }),
  ADD_SENSOR: (sensor: OBSensor, uuid: string): AddSensorAction => ({
    type: 'add_sensor',
    sensor,
    uuid,
  }),

  /**
   * Sites
   */
  SET_SITE: (uuid: string, site: OBSite): SetSiteAction => ({
    type: 'set_site',
    site,
    uuid,
  }),
  SET_SITES: (data: RawOBSiteSection): SetSitesAction => ({
    type: 'set_sites',
    sites: data,
  }),
  DELETE_SITE: (uuid: string): DeleteSiteAction => ({
    type: 'delete_site',
    uuid,
  }),

  /**
   * Cloud Sensors
   */
  SET_CLOUD_SENSORS: (
    cloudSensors: RawOBCloudSensors,
  ): SetCloudSensorsAction => ({
    type: 'set_cloud_sensors',
    cloudSensors,
  }),
  EDIT_CLOUD_SENSORS: (
    cloudSensors: OBCloudSensors,
  ): EditCloudSensorsAction => ({
    type: 'edit_cloud_sensors',
    cloudSensors,
  }),

  /**
   * Risk Scanners
   */
  DELETE_SCANNER: (uuid: string): DeleteScannerAction => ({
    type: 'delete_scanner',
    uuid,
  }),
  EDIT_SCANNER: (uuid: string, scanner: OBScanner): EditScannerAction => ({
    type: 'edit_scanner',
    scanner,
    uuid,
  }),
  SET_SCANNERS: (data: {
    scanners: RawOBScanner[];
    completed: boolean;
  }): SetScannersAction => ({
    type: 'set_scanners',
    scanners: data,
  }),
  ADD_SCANNER: (scanner: OBScanner): AddScannerAction => ({
    type: 'add_scanner',
    scanner,
  }),
  SET_SCANNERS_LOADING: (loading: boolean): SetScannersLoadingAction => ({
    type: 'set_scanners_loading',
    loading,
  }),

  /**
   * Virtual Scanner
   */
  SET_VIRTUAL_SCANNER: (
    setting: RawOBVirtualScanner,
  ): SetVirtualScannerAction => ({
    type: 'set_virtual_scanner',
    setting,
  }),
  EDIT_VIRTUAL_SCANNER: (
    setting: OBVirtualScanner,
  ): EditVirtualScannerAction => ({
    type: 'edit_virtual_scanner',
    setting,
  }),

  /**
   * Endpoint Config
   */
  SET_ENDPOINT: (config: RawOBEndpointConfig): SetEndpointAction => ({
    type: 'set_endpoint',
    config,
  }),
  EDIT_ENDPOINT: (config: OBEndpointConfig): EditEndpointAction => ({
    type: 'edit_endpoint',
    config,
  }),

  /**
   * Virtual Sensor Config
   */
  SET_VIRTUAL_SENSOR: (
    config: RawOBVirtualSensorConfig,
  ): SetVirtualSensorAction => ({
    type: 'set_virtual_sensor',
    config,
  }),
  EDIT_VIRTUAL_SENSOR: (
    config: OBVirtualSensorConfig,
  ): EditVirtualSensorAction => ({
    type: 'edit_virtual_sensor',
    config,
  }),

  TOGGLE_COMPLETE: (key: keyof CompletableSection): ToggleCompleteAction => ({
    type: 'toggle_complete',
    key,
  }),

  SET_ONBOARDING_AVAILABLE_ENDPOINTS: (
    availableEndpoints: RawOBAvailableEndpoints,
  ): SetOnboardingAvailableEndpoints => ({
    type: 'set_onboarding_available_endpoints',
    availableEndpoints: availableEndpoints,
  }),

  SET_ONBOARDING_AVAILABLE_VIRTUAL_SENSORS: (
    availableVirtualSensors: RawOBAvailableVirtualSensors,
  ): SetOnboardingAvailableVirtualSensors => ({
    type: 'set_onboarding_available_virtual_sensors',
    availableVirtualSensors: availableVirtualSensors,
  }),

  TOGGLE_CLOUD_SENSOR_COMPLETE: (
    key: OBCloudSensorApplication,
  ): ToggleCloudSensorCompleteAction => ({
    type: 'toggle_cloud_sensor_complete',
    key,
  }),

  SET_SYNC_RESULTS: (
    results: OBSyncResults | null,
  ): SetOnboardingSyncResultsAction => ({
    type: 'set_sync_results',
    results,
  }),

  SET_ONBOARDING_TEAM_CONTACTS: (
    contacts: OBTeamContacts,
  ): SetOnboardingTeamContactsAction => ({
    type: 'set_onboarding_team_contacts',
    contacts,
  }),

  SET_ONBOARDING_SYNC_MODAL_OPEN: (
    open: boolean,
  ): SetOnboardingSyncModalOpen => ({
    type: 'set_onboarding_sync_modal_open',
    open,
  }),

  SET_ONBOARDING_PRODUCTS: (
    products: OBRawProducts,
  ): SetOnboardingProducts => ({
    type: 'set_onboarding_products',
    products,
  }),

  SET_ONBOARDING_AVAILABLE_HARDWARE: (
    availableHardware: OBAvailableHardware,
  ): SetOnboardingAvailableHardware => ({
    type: 'set_onboarding_available_hardware',
    availableHardware: availableHardware,
  }),

  SET_ONBOARDING_AVAILABLE_HARDWARE_QUANTITY: (
    hardwareQuantity: DeviceQuantityMap,
  ): SetOnboardingAvailableHardwareQuantity => ({
    type: 'set_onboarding_available_hardware_quantity',
    hardwareQuantity: hardwareQuantity,
  }),

  SET_ONBOARDING_AVAILABLE_GENERIC_SENSORS: (availableGenericSensors: {
    series: OBGenericSensor[];
  }): SetOnboardingAvailableGenericSensors => ({
    type: 'set_onboarding_available_generic_sensors',
    availableGenericSensors: availableGenericSensors.series,
  }),

  SET_ONBOARDING_SENSOR_CAPABILITIES: (
    seriesId: string,
    capabilities: SensorCapability[],
  ): SetOnboardingSensorCapabilities => ({
    type: 'set_onboarding_sensor_capabilities',
    sensorCapabilities: { [seriesId]: capabilities },
  }),
};

export const ONBOARDING_THUNK_ACTIONS = {
  SUBMIT_ONBOARDING_DATA: () => {
    return async (dispatch: Dispatch) => {
      try {
        await dispatch(apiRequest.saveDataEntryCompleted());
        await dispatch(apiRequest.getUserInfo());
      } catch (error) {
        // the individual api requests will handle their respective errors
      }
    };
  },
};
